/**
 * src/api/votes.js
 *
 * Generic up/down vote API. Shared per tenant, not per user. A subject is
 * `subjectType` plus id fields (`ticketId`/`skillId` today); `subjectName`/
 * `subjectDescription` are display copies, not used for matching.
 * Bearer token is attached automatically from the auth cookie.
 */

import { authedJson } from './http'

const apiGet = path => authedJson('GET', path)
const apiPost = (path, body) => authedJson('POST', path, body)

/**
 * Fetch the tenant's current shared vote for a subject. Pass whichever id fields
 * apply to `subjectType` — leave the rest undefined.
 * GET /api/votes?subject_type={subjectType}&ticket_id={ticketId}&skill_id={skillId}
 * @param {string} subjectType
 * @param {{ ticketId?: string, skillId?: number|string }} [ids]
 * @returns {Promise<{
 *   subject_type: string, ticket_id: string|null, skill_id: number|null,
 *   subject_name: string|null, subject_description: string|null,
 *   vote: -1|0|1, voted_by: number|null, voted_by_name: string|null, updated_at: string|null
 * }>}
 */
export async function fetchVote(subjectType, { ticketId, skillId } = {}) {
  const params = new URLSearchParams({ subject_type: subjectType })
  if (ticketId != null) params.set('ticket_id', ticketId)
  if (skillId != null) params.set('skill_id', skillId)
  return apiGet(`/votes?${params.toString()}`)
}

/**
 * Cast the tenant's shared vote on a subject. Casting the same direction again
 * clears the vote (server toggles it off); casting the opposite direction flips it.
 * POST /api/votes
 * @param {string} subjectType
 * @param {{ ticketId?: string, skillId?: number|string }} ids — the same id fields passed to fetchVote
 * @param {string} subjectName — display label, e.g. the skill's name
 * @param {string|null} [subjectDescription] — display description, e.g. the skill's reason text
 * @param {1|-1} vote
 * @returns {Promise<{
 *   subject_type: string, ticket_id: string|null, skill_id: number|null,
 *   subject_name: string|null, subject_description: string|null,
 *   vote: -1|0|1, voted_by: number|null, voted_by_name: string|null, updated_at: string|null
 * }>}
 */
export async function castVote(subjectType, { ticketId, skillId } = {}, subjectName, subjectDescription, vote) {
  return apiPost('/votes', {
    subject_type: subjectType,
    ticket_id: ticketId ?? null,
    skill_id: skillId ?? null,
    subject_name: subjectName,
    subject_description: subjectDescription ?? null,
    vote,
  })
}
